import * as React from 'react';
import { Editor } from 'slate-react';
import { Value, Block, Mark, MarkProperties } from 'slate';
import { OrderedSet } from 'immutable';
import isEqual from 'lodash.isequal';
import { Dispatch } from 'react-redux';

import { STYLE_TYPES, MARK_TYPES, TEXT_ALIGN } from './helpers/constants';
import helpers from './helpers';
import { StyledEditorWrapper, StyledEditor } from './styles';
import { StoreState } from '../../../reducers';
import { IGNORE_PAGE_CLICK_CLASS } from '../Page';
import { getBorderWidth } from '../../../containers/Editor/utils/helpers';
import { updateEditableTextField, setActiveItem, toggleEditPanel } from '../../../containers/Editor/actions';
import EditPanel from '../../../containers/Editor/EditPanel';
import TextPanel from '../../../containers/Editor/EditPanel/TextPanel';
import LinkPanel from '../../../containers/Editor/EditPanel/LinkPanel';
import { EditorType, EditableType } from '../../../containers/Editor/constants';
import Utils from '../../../utils';

interface TextAlignFormat {
	textAlign: string;
	textAlignLast: string;
}

interface LineHeightFormat {
	lineHeight: string;
	minHeight: string;
}

interface RichTextEditorProps {
	editorType: EditorType;
	page: number;
	id: string;
	width?: number | string;
	height?: number | string;
	pageScale?: number;
	selfScale?: number;
	transformStyle?: string;
	content: string;
	isActive: boolean;
	parentId: string | null;
	activeParentId: string | null;
	isEditPanelDisplayed: boolean;
	shouldShowScrollbar: boolean;
	dispatch: Dispatch<StoreState>;
}

interface RichTextEditorState {
	value: Value;
}

class RichTextEditor extends React.Component<RichTextEditorProps, RichTextEditorState> {
	private editor: Editor;
	private currentMarks: OrderedSet<Mark>;
	private textPanelActions;

	constructor(props: RichTextEditorProps) {
		super(props);

		const DEFAULT_CONTENT = '<p></p>';

		this.state = {
			value: helpers.html.deserialize(props.content || DEFAULT_CONTENT),
		};

		this.currentMarks = this.state.value.marks;

		this.textPanelActions = {
			onFontFamilyChange: this.handleMarkChange.bind(this, MARK_TYPES.get(STYLE_TYPES.FONT_FAMILY)),
			onFontSizeChange: this.handleMarkChange.bind(this, MARK_TYPES.get(STYLE_TYPES.FONT_SIZE)),
			onColorChange: this.handleMarkChange.bind(this, MARK_TYPES.get(STYLE_TYPES.COLOR)),
			onLineHeightChange: this.handleLineHeightChange.bind(this),
			onLetterSpacingChange: this.handleMarkChange.bind(this, MARK_TYPES.get(STYLE_TYPES.LETTER_SPACING)),
			onFontWeightClick: this.handleMarkChange.bind(this, MARK_TYPES.get(STYLE_TYPES.FONT_WEIGHT)),
			onFontStyleClick: this.handleMarkChange.bind(this, MARK_TYPES.get(STYLE_TYPES.FONT_STYLE)),
			onTextDecorationClick: this.handleMarkChange.bind(this, MARK_TYPES.get(STYLE_TYPES.TEXT_DECORATION)),
			onTextAlignLeftClick: this.handleTextAlignChange.bind(this, TEXT_ALIGN.LEFT),
			onTextAlignCenterClick: this.handleTextAlignChange.bind(this, TEXT_ALIGN.CENTER),
			onTextAlignRightClick: this.handleTextAlignChange.bind(this, TEXT_ALIGN.RIGHT),
			onTextAlignJustifyClick: this.handleTextAlignChange.bind(this, TEXT_ALIGN.JUSTIFY),
		};

		this.handleChange = this.handleChange.bind(this);
		this.handleMouseDown = this.handleMouseDown.bind(this);
		this.handleLinkChange = this.handleLinkChange.bind(this);
	}

	shouldComponentUpdate(nextProps: RichTextEditorProps) {
		const { isActive, shouldShowScrollbar } = this.props;
		// we have to update only active RTE for proper focus handling
		return isActive ||
			isActive !== nextProps.isActive ||
			shouldShowScrollbar !== nextProps.shouldShowScrollbar;
	}

	componentDidUpdate(prevProps: RichTextEditorProps) {
		const { isActive, parentId, activeParentId, isEditPanelDisplayed } = this.props;
		const isBecomeActive = !prevProps.isActive && isActive;
		const isBecomeInActive = prevProps.isActive && !isActive;

		if (!prevProps.isEditPanelDisplayed && isEditPanelDisplayed) {
			this.hideTouchKeyboard();
		}

		if (isBecomeInActive) {
			this.hideTouchKeyboard();
			this.handleBlur();
		}

		if (parentId) {
			const isParentIdChanged = prevProps.activeParentId !== activeParentId;
			const parentContainer = document.querySelector(`#${parentId}`);

			if (parentContainer) {
				if (isBecomeActive) {
					parentContainer.classList.add('is-visible');
				}
				if (isBecomeInActive && isParentIdChanged) {
					parentContainer.classList.remove('is-visible');
				}
			}
		}
	}

	hideTouchKeyboard() {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	}

	handleChange({ value }: { value: Value }) {
		const { isActive, editorType } = this.props;

		if (!isActive) {
			return;
		}

		if (editorType === EditorType.INDESIGN) {
			const shouldUpdateCurrentMarks = value.marks.size && !isEqual(this.currentMarks, value.marks);
			if (shouldUpdateCurrentMarks) {
				this.currentMarks = value.marks;
			}

			this.setState({ value }, () => {
				// By default Slate removes all marks when there is no text in paragraph. We need to keep previous marks and apply them for proper text format
				const shouldCorrectMarks = !value.marks.size && !!this.currentMarks.size;
				if (shouldCorrectMarks) {
					this.editor.withoutSaving(() => {
						this.currentMarks.forEach((mark: Mark) => this.editor.addMark(mark));
					});
				}
			});
		} else {
			this.setState({ value });
		}
	}

	handleBlur() {
		const { content, page, id, dispatch } = this.props;
		const { value } = this.state;
		const serializedContent = helpers.html.serialize(value);
		const isContentChanged = serializedContent !== content;

		if (isContentChanged) {
			dispatch(updateEditableTextField(page, id, {
				content: serializedContent,
			}));
		}
	}

	handleMouseDown() {
		const { id, parentId, isActive, isEditPanelDisplayed, dispatch } = this.props;
		if (!isActive) {
			dispatch(setActiveItem(id, parentId));

			if (!isEditPanelDisplayed) {
				dispatch(toggleEditPanel());
			}
		}
	}

	handleMarkChange(type: STYLE_TYPES, param: string) {
		const { value: { marks, activeMarks } } = this.state;
		let newMark: MarkProperties;

		switch (type) {
			case MARK_TYPES.get(STYLE_TYPES.FONT_FAMILY):
				newMark = helpers.getFontFamilyMark(param);
				break;
			case MARK_TYPES.get(STYLE_TYPES.FONT_SIZE):
				newMark = helpers.getFontSizeMark(param);
				break;
			case MARK_TYPES.get(STYLE_TYPES.COLOR):
				newMark = helpers.getColorMark(param);
				break;
			case MARK_TYPES.get(STYLE_TYPES.FONT_STYLE):
				newMark = helpers.getFontStyleMark(activeMarks);
				break;
			case MARK_TYPES.get(STYLE_TYPES.FONT_WEIGHT):
				newMark = helpers.getFontWeightMark(activeMarks);
				break;
			case MARK_TYPES.get(STYLE_TYPES.TEXT_DECORATION):
				newMark = helpers.getTextDecorationMark(activeMarks);
				break;
			case MARK_TYPES.get(STYLE_TYPES.LETTER_SPACING):
				newMark = helpers.getLetterSpacingMark(param);
				break;
			default:
				return;
		}

		helpers.getMarksByType(marks, type).map((mark: Mark) => this.editor.removeMark(mark));
		this.editor.addMark(newMark);

		setTimeout(() => {
			this.hideTouchKeyboard();
		}, 0);
	}

	handleBlockFormatChange(data: TextAlignFormat | LineHeightFormat) {
		const { value: { blocks } } = this.state;

		if (blocks) {
			blocks.forEach(({ key, type, data: currentData }: Block) => {
				const newData = {
					style: {
						...currentData.get('style'),
						...data,
					},
				};
				this.editor.setNodeByKey(key, {
					type,
					data: newData,
				});
			});
		}

		setTimeout(() => {
			this.hideTouchKeyboard();
		}, 0);
	}

	handleTextAlignChange(textAlign: string) {
		this.handleBlockFormatChange({
			textAlign,
			textAlignLast: 'auto',
		});
	}

	handleLineHeightChange(lineHeight: string) {
		this.handleBlockFormatChange({
			lineHeight,
			minHeight: lineHeight,
		});
	}

	handleLinkChange(href: string) {
		const { value } = this.state;
		helpers.updateLink(this.editor, value, href);
	}

	renderEditPanel() {
		const { isActive, isEditPanelDisplayed } = this.props;
		const { value } = this.state;
		const { blocks, inlines, selection, marks, activeMarks } = value;

		return isActive && isEditPanelDisplayed && (
			<EditPanel
				defaultType={EditableType.text}
				panels={{
					[EditableType.text]: (
						<TextPanel
							fontFamily={helpers.getFontFamily(marks)}
							fontSize={helpers.getFontSize(marks)}
							color={helpers.getColor(marks)}
							lineHeight={helpers.getLineHeight(blocks)}
							letterSpacing={helpers.getLetterSpacing(marks)}
							isBoldActive={helpers.isFontWeightBold(activeMarks)}
							isItalicActive={helpers.isFontStyleItalic(activeMarks)}
							isUnderlineActive={helpers.isTextDecorationUnderline(activeMarks)}
							isTextAlignLeft={helpers.isTextAlignLeft(blocks)}
							isTextAlignCenter={helpers.isTextAlignCenter(blocks)}
							isTextAlignRight={helpers.isTextAlignRight(blocks)}
							isTextAlignJustify={helpers.isTextAlignJustify(blocks)}
							actions={this.textPanelActions}
						/>
					),
					[EditableType.link]: (
						<LinkPanel
							value={helpers.getLinkHref(inlines)}
							canAddLink={selection.isExpanded}
							onChange={this.handleLinkChange}
						/>
					),
				}}
			/>
		);
	}

	render() {
		const {
			editorType,
			isActive,
			width,
			height,
			transformStyle,
			pageScale = 1,
			selfScale = 1,
			shouldShowScrollbar,
		} = this.props;
		const { value } = this.state;

		// is needed for rendering in iframe (for html editor)
		// width is set on parent container to avoid incorrect width calculation in case it is set in %
		const style = {
			height,
		};

		const editorClasses = Utils.getCssClasses(`${IGNORE_PAGE_CLICK_CLASS} uprez-rte-wrapper`, {
			'is-active': isActive,
		});

		return (
			<StyledEditorWrapper
				className={editorClasses}
				editorType={editorType}
				isActive={isActive}
				width={width}
				height={height}
				transform={transformStyle}
				outlineWidth={getBorderWidth(pageScale * selfScale)}
				style={editorType === EditorType.HTML ? style : undefined}
			>
				<StyledEditor
					ref={(editor: Editor) => { this.editor = editor; }}
					className="uprez-rte"
					shouldShowScrollbar={shouldShowScrollbar}
					value={value}
					onMouseDown={this.handleMouseDown}
					onTouchStart={this.handleMouseDown}
					onChange={this.handleChange}
					onBeforeInput={helpers.handleBeforeInput}
					onCopy={helpers.handleCopy}
					onCut={helpers.handleCopy}
					onPaste={helpers.handlePaste}
					onKeyDown={helpers.handleKeyDown}
					renderBlock={helpers.renderBlock}
					renderInline={helpers.renderInline}
					renderMark={helpers.renderMark}
				/>
				{this.renderEditPanel()}
			</StyledEditorWrapper>
		);
	}
}

export default RichTextEditor;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/RichTextEditor/index.tsx